import type { DeepPartial } from "./types";

/**
 * Check if a value is a plain object (not null, array, or other object types)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}

/**
 * Deep merge two objects, with the override taking precedence.
 * Arrays are replaced entirely (not merged).
 *
 * @param base - The base object with default values
 * @param override - The override object with custom values
 * @returns A new object with merged values
 */
export function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override?: DeepPartial<T>,
): T {
  if (!override) {
    return { ...base };
  }

  const result = { ...base } as T;

  for (const key of Object.keys(override) as Array<keyof T>) {
    const baseValue = base[key];
    const overrideValue = override[key as keyof typeof override];

    if (overrideValue === undefined) {
      continue;
    }

    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      result[key] = deepMerge(
        baseValue as Record<string, unknown>,
        overrideValue as DeepPartial<Record<string, unknown>>,
      ) as T[keyof T];
    } else {
      result[key] = overrideValue as T[keyof T];
    }
  }

  return result;
}
